"use client";

import { useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  Check,
  ChevronRight,
  ClipboardCopy,
  LoaderCircle,
  Save,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type ManualIntake = {
  theme: string;
  route: string;
  sourceForm: string;
  transformationDistance: string;
  primaryThemeLane: string;
  secondaryThemeLane: string;
  emotionalGenre: string;
  pressureArena: string;
  humanQuestion: string;
  storyStatus: string;
  platform: string;
  aspectRatio: string;
  productionProfile: string;
  pacing: string;
  duration: string;
};

type FunnelStage = "CONCEPTS_12" | "BLUEPRINTS_3" | "WINNER_1";

type ManualDraft = {
  funnelStage: FunnelStage;
  conceptResult: string;
  shortlist: string;
  conceptsSaved: boolean;
  blueprintResult: string;
  winnerChoice: string;
  blueprintsSaved: boolean;
  winnerResult: string;
  winnerSaved: boolean;
  productionStage: number;
  productionPart: number;
  productionResults: Record<string, string>;
  productionSaved: Record<string, boolean>;
};

const emptyDraft: ManualDraft = {
  funnelStage: "CONCEPTS_12",
  conceptResult: "",
  shortlist: "",
  conceptsSaved: false,
  blueprintResult: "",
  winnerChoice: "",
  blueprintsSaved: false,
  winnerResult: "",
  winnerSaved: false,
  productionStage: 6,
  productionPart: 1,
  productionResults: {},
  productionSaved: {},
};

const productionStages = [
  [6, "Story Bible", "Create the authoritative character, relationship, theme, moral-boundary, and story-rule bible."],
  [7, "World and Prop Bible", "Define locations, social systems, recurring props, visual rules, and continuity locks."],
  [8, "Causality and story systems", "Build the cause-and-effect chain so every major event is earned and consequential."],
  [9, "Route-specific blueprint", "Complete a route-faithful blueprint without drifting from the approved story authority."],
  [10, "Emotional and click promise", "Define the honest audience promise, emotional promise, curiosity engine, and payoff obligation."],
  [11, "Retention Blueprint", "Design escalating reveals, reversals, questions, micro-payoffs, and attention resets."],
  [12, "Drama-First architecture", "Build premise-earned act architecture led by conflict and choice rather than formula."],
  [13, "Scene-by-scene outline", "Create the full ordered scene outline with purpose, conflict, turn, and consequence."],
  [14, "Beat-by-beat structure", "Break every approved scene into paced dramatic beats and five-second shot intentions."],
  [15, "Route fidelity", "Audit the work against the selected Route O or Route T rules and identify required repairs."],
  [16, "Channel originality audit", "Check interchangeability, repeated combinations, ending family, and catalogue collision risks."],
  [17, "Story and feasibility audits", "Audit logic, emotion, production feasibility, continuity, viewer value, and narrative satisfaction."],
  [18, "Pre-screenplay originality gate", "Issue a GREEN, YELLOW, or RED decision with evidence and mandatory repairs."],
  [19, "Save blueprint TXT", "Assemble the approved blueprint as one clean, production-ready text master."],
  [20, "Explicit blueprint approval", "Prepare a concise human approval checklist; do not approve on the human's behalf."],
  [21, "Screenplay-writer handoff", "Create the complete handoff pack for the approved screenplay model."],
  [22, "Screenplay", "Write dialogue, action, and shot-aware scenes from the approved blueprint only."],
  [23, "Final screenplay audit", "Audit the screenplay against its blueprint, catalogue risks, pacing, continuity, and ending obligations."],
  [24, "Production and closeout", "Prepare production, packaging, publication, disclosure, and database-closeout records."],
] as const;

function normalizeNumbers(value: string, maximum: number): number[] {
  return Array.from(new Set(value.match(/\d+/g)?.map(Number) ?? []))
    .filter((number) => number >= 1 && number <= maximum)
    .sort((a, b) => a - b);
}

function selectNumberedLines(source: string, numbers: number[]): string {
  const wanted = new Set(numbers);
  const selected = source
    .split(/\r?\n/)
    .filter((line) => {
      const match = line.trim().match(/^(?:CONCEPT|BLUEPRINT)?\s*0?(\d{1,2})\s*(?:[|.):-]|$)/i);
      return match ? wanted.has(Number(match[1])) : false;
    });
  return selected.length === numbers.length ? selected.join("\n") : source.trim();
}

function intakeSummary(intake: ManualIntake): string {
  return [
    `Creative direction: ${intake.theme.trim()}`,
    `Route: ${intake.route}${intake.route === "T" ? ` / ${intake.sourceForm} / ${intake.transformationDistance}` : ` / primary ${intake.primaryThemeLane} / secondary ${intake.secondaryThemeLane}`}`,
    `Emotional genre: ${intake.emotionalGenre}`,
    `Pressure arena: ${intake.pressureArena}`,
    `Human question: ${intake.humanQuestion.trim() || "Infer one precise question from the brief"}`,
    `Format: ${intake.storyStatus}; ${intake.platform}; ${intake.aspectRatio}; ${intake.duration} minutes; pacing ${intake.pacing}`,
    `Production profile: ${intake.productionProfile}`,
  ].join("\n");
}

function conceptPrompt(intake: ManualIntake): string {
  return `RUNYI AWARA FILMS — MANUAL CONCEPT FUNNEL\n\n${intakeSummary(intake)}\n\nGenerate exactly 12 genuinely different movie concepts. Vary protagonist contradiction, relationship dynamic, pressure mechanism, decisive climax, and ending family. Protect human value and narrative satisfaction. Do not claim you searched a database; the dashboard performs catalogue checks separately.\n\nReturn exactly 12 physical lines in this format:\n01 | TITLE | one-sentence logline | viewer hook | moral tension | ending family\n\nNo introduction, commentary, ranking, or repeated concepts.`;
}

function blueprintPrompt(intake: ManualIntake, conceptResult: string, shortlist: string): string {
  const choices = normalizeNumbers(shortlist, 12);
  return `RUNYI AWARA FILMS — SHORTLIST BLUEPRINTS\n\n${intakeSummary(intake)}\n\nThe human selected concepts ${choices.join(", ")} from the twelve-concept round:\n${selectNumberedLines(conceptResult, choices)}\n\nExpand exactly these 3 selections into distinct compact blueprints. Preserve their differences. Each blueprint must state protagonist contradiction, pressure engine, relationship arc, midpoint reversal, climax choice, ending, originality risks, and viewer value.\n\nReturn exactly 3 physical lines, numbered 1–3, in this format:\nBLUEPRINT 1 | source concept number | title | complete compact blueprint\n\nNo introduction or recommendation.`;
}

function winnerPrompt(intake: ManualIntake, blueprintResult: string, winnerChoice: string): string {
  const choice = normalizeNumbers(winnerChoice, 3)[0];
  return `RUNYI AWARA FILMS — APPROVED CONCEPT AUTHORITY\n\n${intakeSummary(intake)}\n\nThe human approved blueprint ${choice}:\n${selectNumberedLines(blueprintResult, [choice])}\n\nCreate the single authoritative winner record. Include final title, logline, protagonist contradiction, relationship engine, stakes, human question, climax choice, ending family, viewer promise, and non-negotiable originality locks. Do not assign an RAF number. This winner remains in the temporary Concept Registry until the approved blueprint reaches canonical Stage 22 / supporting Step 34.\n\nReturn only the winner record.`;
}

function productionPrompt(
  intake: ManualIntake,
  stage: number,
  part: number,
  priorArtifact: string,
): string {
  const entry = productionStages.find(([number]) => number === stage) ?? productionStages[0];
  const screenplayPartRule = stage === 22
    ? intake.productionProfile === "HUNDRED_SCENE"
      ? `Write only Part ${part} of 20. Each part contains exactly 5 sequential scenes. Do not repeat earlier scenes.`
      : `Write only manageable screenplay Part ${part}. State the included scene range and do not repeat earlier scenes.`
    : "Return one complete stage artifact without conversational commentary.";
  return `RUNYI AWARA FILMS — CANONICAL STAGE ${stage}: ${entry[1].toUpperCase()}\n\n${intakeSummary(intake)}\n\nStage purpose: ${entry[2]}\n\nApproved prior artifact:\n${priorArtifact.trim()}\n\n${screenplayPartRule}\nUse only the supplied authority. Flag contradictions instead of silently inventing repairs. Keep Route ${intake.route}, pacing ${intake.pacing}, the approved ending obligation, and the human-value gate intact. Do not claim database access. Do not assign or invent an RAF number.\n\nReturn only the Stage ${stage} artifact${stage === 22 ? `, Part ${part}` : ""}.`;
}

export function ManualChatGPTDesk({
  runId,
  intake,
  supabase,
}: {
  runId: string;
  intake: ManualIntake;
  supabase: SupabaseClient;
}) {
  const [draft, setDraft] = useState<ManualDraft>(emptyDraft);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        const cached = window.localStorage.getItem(`runyi:manual:${runId}`);
        setDraft(cached ? { ...emptyDraft, ...JSON.parse(cached) } : emptyDraft);
      } catch {
        setDraft(emptyDraft);
      } finally {
        setDraftLoaded(true);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [runId]);

  useEffect(() => {
    if (!draftLoaded) return;
    const timer = window.setTimeout(() => {
      window.localStorage.setItem(`runyi:manual:${runId}`, JSON.stringify(draft));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [draft, draftLoaded, runId]);

  const shortlistNumbers = useMemo(() => normalizeNumbers(draft.shortlist, 12), [draft.shortlist]);
  const winnerNumbers = useMemo(() => normalizeNumbers(draft.winnerChoice, 3), [draft.winnerChoice]);
  const funnelPrompt = useMemo(() => {
    if (draft.funnelStage === "BLUEPRINTS_3") return blueprintPrompt(intake, draft.conceptResult, draft.shortlist);
    if (draft.funnelStage === "WINNER_1") return winnerPrompt(intake, draft.blueprintResult, draft.winnerChoice);
    return conceptPrompt(intake);
  }, [draft.blueprintResult, draft.conceptResult, draft.funnelStage, draft.shortlist, draft.winnerChoice, intake]);

  const productionKey = `${draft.productionStage}:${draft.productionPart}`;
  const currentProductionResult = draft.productionResults[productionKey] ?? "";
  const priorProductionArtifact = useMemo(() => {
    if (draft.productionStage === 6) return draft.winnerResult;
    for (let stage = draft.productionStage - 1; stage >= 6; stage -= 1) {
      const keys = Object.keys(draft.productionResults)
        .filter((key) => key.startsWith(`${stage}:`) && draft.productionResults[key]?.trim())
        .sort((a, b) => Number(b.split(":")[1]) - Number(a.split(":")[1]));
      if (keys[0]) return draft.productionResults[keys[0]];
    }
    return draft.winnerResult;
  }, [draft.productionResults, draft.productionStage, draft.winnerResult]);
  const currentProductionPrompt = useMemo(
    () => productionPrompt(intake, draft.productionStage, draft.productionPart, priorProductionArtifact),
    [draft.productionPart, draft.productionStage, intake, priorProductionArtifact],
  );

  async function copyPrompt(prompt: string, key: string) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(key);
      setNotice("Prompt copied. Open ChatGPT, paste it, then bring the answer back here.");
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      setNotice("Press and hold inside the prompt box, then choose Copy.");
    }
  }

  async function saveStage({
    stage,
    prompt,
    result,
    selection,
    part = 1,
    approved,
  }: {
    stage: string;
    prompt: string;
    result: string;
    selection?: string;
    part?: number;
    approved: boolean;
  }) {
    if (result.trim().length < 40) {
      setNotice("Paste the complete ChatGPT result before saving this stage.");
      return false;
    }
    setSaving(true);
    setNotice(null);
    const { error } = await supabase.rpc("runyi_save_manual_workflow_stage", {
      p_concept_run_id: runId,
      p_workflow_stage: stage,
      p_prompt_text: prompt,
      p_result_text: result.trim(),
      p_selection_text: selection?.trim() || null,
      p_part_number: part,
      p_human_approved: approved,
    });
    setSaving(false);
    if (error) {
      const needsUpgrade = error.code === "PGRST202" || /runyi_save_manual_workflow_stage|schema cache/i.test(error.message);
      setNotice(needsUpgrade
        ? "Your draft is safe on this phone. Install database upgrade 07 before saving it permanently."
        : error.message);
      return false;
    }
    setNotice("Saved to the temporary workflow record. No RAF number was assigned.");
    return true;
  }

  async function saveFunnelStage() {
    if (draft.funnelStage === "CONCEPTS_12") {
      if ((draft.conceptResult.match(/^(?:CONCEPT\s*)?0?\d{1,2}\s*[|.):-]/gim) ?? []).length < 12) {
        setNotice("The returned text should contain 12 numbered concept lines.");
        return;
      }
      if (await saveStage({ stage: "CONCEPTS_12", prompt: funnelPrompt, result: draft.conceptResult, approved: false })) {
        setDraft((current) => ({ ...current, conceptsSaved: true, funnelStage: "BLUEPRINTS_3" }));
      }
      return;
    }
    if (draft.funnelStage === "BLUEPRINTS_3") {
      if (shortlistNumbers.length !== 3) {
        setNotice("Enter exactly three different concept numbers between 1 and 12.");
        return;
      }
      if (await saveStage({ stage: "BLUEPRINTS_3", prompt: funnelPrompt, result: draft.blueprintResult, selection: shortlistNumbers.join(","), approved: true })) {
        setDraft((current) => ({ ...current, blueprintsSaved: true, funnelStage: "WINNER_1" }));
      }
      return;
    }
    if (winnerNumbers.length !== 1) {
      setNotice("Choose exactly one blueprint number between 1 and 3.");
      return;
    }
    if (await saveStage({ stage: "WINNER_1", prompt: funnelPrompt, result: draft.winnerResult, selection: String(winnerNumbers[0]), approved: true })) {
      setDraft((current) => ({ ...current, winnerSaved: true }));
    }
  }

  async function saveProductionStage() {
    const saved = await saveStage({
      stage: `CANONICAL_${String(draft.productionStage).padStart(2, "0")}`,
      prompt: currentProductionPrompt,
      result: currentProductionResult,
      part: draft.productionPart,
      approved: true,
    });
    if (!saved) return;
    setDraft((current) => ({
      ...current,
      productionSaved: { ...current.productionSaved, [productionKey]: true },
    }));
  }

  const resultValue = draft.funnelStage === "CONCEPTS_12"
    ? draft.conceptResult
    : draft.funnelStage === "BLUEPRINTS_3"
      ? draft.blueprintResult
      : draft.winnerResult;
  const setResultValue = (value: string) => setDraft((current) => draft.funnelStage === "CONCEPTS_12"
    ? { ...current, conceptResult: value, conceptsSaved: false }
    : draft.funnelStage === "BLUEPRINTS_3"
      ? { ...current, blueprintResult: value, blueprintsSaved: false }
      : { ...current, winnerResult: value, winnerSaved: false });

  return (
    <section className="space-y-5 rounded-2xl border border-primary/22 bg-gradient-to-br from-card to-background p-4 shadow-2xl shadow-black/20 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2"><Sparkles className="size-5 text-primary" /><p className="font-semibold">Manual ChatGPT desk</p></div>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground/52">No API credits are used. Copy one controlled prompt, run it in the ChatGPT app, paste the answer back, and save the human-approved stage.</p>
        </div>
        <Badge variant="outline" className="w-fit border-emerald-400/25 bg-emerald-400/8 text-emerald-300">No API cost</Badge>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 rounded-xl border border-foreground/8 bg-[var(--surface-inset)] p-3 text-center">
        {([[
          "CONCEPTS_12", "12", "Concepts", draft.conceptsSaved,
        ], [
          "BLUEPRINTS_3", "3", "Blueprints", draft.blueprintsSaved,
        ], [
          "WINNER_1", "1", "Winner", draft.winnerSaved,
        ]] as const).map(([stage, value, label, saved], index) => (
          <div key={stage} className="contents">
            {index > 0 && <ChevronRight className="size-4 text-foreground/20" />}
            <button
              type="button"
              onClick={() => setDraft((current) => ({ ...current, funnelStage: stage }))}
              className={`rounded-lg px-2 py-2 ${draft.funnelStage === stage ? "bg-primary/12 text-primary" : "text-foreground/52"}`}
            >
              <span className="mx-auto flex size-8 items-center justify-center rounded-full border border-current/30 text-sm font-bold">{saved ? <Check className="size-4" /> : value}</span>
              <span className="mt-1 block text-[11px]">{label}</span>
            </button>
          </div>
        ))}
      </div>

      {draft.funnelStage === "BLUEPRINTS_3" && (
        <Field label="Your three concept numbers" hint="Example: 2, 6, 11">
          <Input value={draft.shortlist} onChange={(event) => setDraft((current) => ({ ...current, shortlist: event.target.value, blueprintsSaved: false }))} inputMode="numeric" placeholder="2, 6, 11" className="h-12 border-foreground/12 bg-[var(--surface-inset)] text-[16px] text-foreground" />
        </Field>
      )}
      {draft.funnelStage === "WINNER_1" && (
        <Field label="Approved blueprint number" hint="Choose 1, 2, or 3">
          <Input value={draft.winnerChoice} onChange={(event) => setDraft((current) => ({ ...current, winnerChoice: event.target.value, winnerSaved: false }))} inputMode="numeric" placeholder="1" className="h-12 border-foreground/12 bg-[var(--surface-inset)] text-[16px] text-foreground" />
        </Field>
      )}

      <CopyBox title="1. Copy this prompt" value={funnelPrompt} copied={copied === draft.funnelStage} onCopy={() => copyPrompt(funnelPrompt, draft.funnelStage)} />
      <Field label="2. Paste the ChatGPT answer here" hint={`${resultValue.length.toLocaleString()} characters`}>
        <Textarea value={resultValue} onChange={(event) => setResultValue(event.target.value)} placeholder="Paste the complete ChatGPT answer…" className="min-h-44 border-foreground/12 bg-[var(--surface-inset)] text-[16px] leading-6 text-foreground placeholder:text-foreground/25" />
      </Field>
      <Button onClick={saveFunnelStage} disabled={saving} className="h-12 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
        {saving ? <LoaderCircle className="animate-spin" /> : <Save />}
        {draft.funnelStage === "CONCEPTS_12" ? "Save 12 concepts and continue" : draft.funnelStage === "BLUEPRINTS_3" ? "Save shortlist and continue" : "Approve winner and unlock production"}
      </Button>

      {notice && <div className="rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-50">{notice}</div>}

      <div className={`space-y-5 border-t border-foreground/10 pt-5 ${draft.winnerSaved ? "" : "opacity-55"}`}>
        <div className="flex items-start gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-emerald-400/10 text-emerald-300"><ShieldCheck className="size-5" /></div>
          <div><p className="font-semibold">Canonical production handoff</p><p className="mt-1 text-sm leading-6 text-foreground/48">Stages 6–24 unlock after the winner is saved. Each answer becomes the authority for the next stage.</p></div>
        </div>

        <div className="grid gap-4 sm:grid-cols-[1fr_150px]">
          <Field label="Production stage">
            <Select disabled={!draft.winnerSaved} value={String(draft.productionStage)} onValueChange={(value) => setDraft((current) => ({ ...current, productionStage: Number(value), productionPart: 1 }))}>
              <SelectTrigger className="h-12 w-full border-foreground/12 bg-[var(--surface-inset)] px-3 text-[16px] text-foreground"><SelectValue /></SelectTrigger>
              <SelectContent className="border-foreground/10 bg-popover text-foreground">{productionStages.map(([number, title]) => <SelectItem key={number} value={String(number)} className="py-3 focus:bg-foreground/10 focus:text-foreground">{number} · {title}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {draft.productionStage === 22 && (
            <Field label="Screenplay part">
              <Input disabled={!draft.winnerSaved} type="number" min="1" max={intake.productionProfile === "HUNDRED_SCENE" ? 20 : 99} value={draft.productionPart} onChange={(event) => setDraft((current) => ({ ...current, productionPart: Math.max(1, Number(event.target.value) || 1) }))} className="h-12 border-foreground/12 bg-[var(--surface-inset)] text-[16px] text-foreground" />
            </Field>
          )}
        </div>

        <CopyBox title="3. Copy the production-stage prompt" value={currentProductionPrompt} copied={copied === productionKey} disabled={!draft.winnerSaved} onCopy={() => copyPrompt(currentProductionPrompt, productionKey)} />
        <Field label="4. Paste this stage result" hint={`${currentProductionResult.length.toLocaleString()} characters`}>
          <Textarea disabled={!draft.winnerSaved} value={currentProductionResult} onChange={(event) => setDraft((current) => ({ ...current, productionResults: { ...current.productionResults, [productionKey]: event.target.value }, productionSaved: { ...current.productionSaved, [productionKey]: false } }))} placeholder="Paste this stage’s ChatGPT answer…" className="min-h-44 border-foreground/12 bg-[var(--surface-inset)] text-[16px] leading-6 text-foreground placeholder:text-foreground/25 disabled:opacity-40" />
        </Field>
        <Button variant="outline" onClick={saveProductionStage} disabled={!draft.winnerSaved || saving} className="h-12 w-full border-foreground/15 bg-foreground/5 text-foreground hover:bg-foreground/10 hover:text-foreground">
          {saving ? <LoaderCircle className="animate-spin" /> : draft.productionSaved[productionKey] ? <Check /> : <Save />}
          {draft.productionSaved[productionKey] ? "Stage saved" : `Save Stage ${draft.productionStage}${draft.productionStage === 22 ? ` · Part ${draft.productionPart}` : ""}`}
        </Button>
      </div>
    </section>
  );
}

function CopyBox({ title, value, copied, disabled = false, onCopy }: { title: string; value: string; copied: boolean; disabled?: boolean; onCopy: () => void }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3"><p className="text-sm font-medium text-foreground/72">{title}</p><Badge variant="outline" className="border-foreground/10 text-foreground/40">Read-only</Badge></div>
      <Textarea readOnly value={value} className="min-h-40 border-foreground/10 bg-background text-[14px] leading-6 text-foreground/70" />
      <Button type="button" variant="outline" disabled={disabled} onClick={onCopy} className="h-11 w-full border-primary/30 bg-primary/8 text-primary hover:bg-primary/14 hover:text-primary">
        {copied ? <Check /> : <ClipboardCopy />}{copied ? "Copied" : "Copy prompt"}
      </Button>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-2 flex items-center justify-between gap-3 text-sm font-medium text-foreground/72"><span>{label}</span>{hint && <span className="text-right text-xs font-normal text-foreground/35">{hint}</span>}</span>{children}</label>;
}
